import { useState, useCallback } from 'react';
import { getErrorMessage } from '@/utils/error';
import useSWR, { mutate } from 'swr';
import { validateFileSize, validateFileExtension, appConfig } from '@/config/app-config';
import type { S3Object, ApiResponse, ListObjectsResponse } from '@/types';
import { getUploadUrl, uploadFileToS3, deleteObjects, moveObject, renameFolder, listObjects, createFolder, getDownloadUrl } from '@/api/client';

// モックモードかどうか
const isMockMode = import.meta.env.VITE_APP_MOCK_MODE === 'true';

interface UseS3ManagerProps {
  currentPrefix: string;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

// モック用のデータ
const mockData: Record<string, { objects: S3Object[]; folders: string[] }> = {};

// モックデータを生成
function getMockData(prefix: string): { objects: S3Object[]; folders: string[] } {
  if (!mockData[prefix]) {
    const baseFolders = [
      `${prefix}reports/`,
      `${prefix}documents/`,
      `${prefix}archives/`,
    ];

    const baseFiles: S3Object[] = [
      {
        key: `${prefix}sample-document.pdf`,
        name: 'sample-document.pdf',
        size: 1024 * 1024 * 2.5,
        lastModified: new Date().toISOString(),
        isFolder: false,
      },
      {
        key: `${prefix}data-export.xlsx`,
        name: 'data-export.xlsx',
        size: 1024 * 512,
        lastModified: new Date(Date.now() - 86400000).toISOString(),
        isFolder: false,
      },
      {
        key: `${prefix}meeting-notes.txt`,
        name: 'meeting-notes.txt',
        size: 1024 * 10,
        lastModified: new Date(Date.now() - 86400000 * 3).toISOString(),
        isFolder: false,
      },
      {
        key: `${prefix}presentation.pptx`,
        name: 'presentation.pptx',
        size: 1024 * 1024 * 5,
        lastModified: new Date(Date.now() - 86400000 * 7).toISOString(),
        isFolder: false,
      },
    ];

    mockData[prefix] = {
      objects: baseFiles,
      folders: baseFolders,
    };
  }
  return mockData[prefix];
}

/**
 * S3ファイル管理フック
 */
export function useS3Manager({ currentPrefix }: UseS3ManagerProps) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [uploadProgresses, setUploadProgresses] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  // SWRでオブジェクト一覧を取得
  const swrKey = currentPrefix ? `s3-objects-${currentPrefix}` : null;

  const fetcher = async (): Promise<ApiResponse<ListObjectsResponse>> => {
    if (isMockMode) {
      // モックデータを返す
      const data = getMockData(currentPrefix);
      return {
        success: true,
        data: {
          objects: data.objects,
          folders: data.folders,
          isTruncated: false,
        },
      };
    }

    // 本番モード: APIを呼び出す
    return listObjects(currentPrefix);
  };

  const { data, error, isLoading } = useSWR<ApiResponse<ListObjectsResponse>>(
    swrKey,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  const objects: S3Object[] = data?.data?.objects || [];
  const folders: string[] = data?.data?.folders || [];

  const [refreshKey, setRefreshKey] = useState(0);

  // 一覧を再取得
  const refresh = useCallback(() => {
    if (swrKey) {
      mutate(swrKey);
    }
    setRefreshKey(prev => prev + 1);
  }, [swrKey]);

  // フォルダ作成
  const handleCreateFolder = useCallback(async (folderName: string): Promise<boolean> => {
    try {
      const normalizedName = folderName.trim().normalize('NFC');
      const folderPath = `${currentPrefix}${normalizedName}/`;

      if (isMockMode) {
        // モックモード: ローカルにフォルダを追加
        const data = getMockData(currentPrefix);
        data.folders.push(folderPath);
        refresh();
        return true;
      }

      const result = await createFolder(folderPath);

      if (result.success) {
        refresh();
        return true;
      } else {
        throw new Error(result.error || 'フォルダの作成に失敗しました');
      }
    } catch (err: unknown) {
      console.error('Failed to create folder:', err);
      throw err;
    }
  }, [currentPrefix, refresh]);

  // ファイルアップロード
  const handleUploadFiles = useCallback(async (files: File[]): Promise<boolean> => {
    // バリデーション
    const invalidSizeFiles = files.filter(f => !validateFileSize(f));
    if (invalidSizeFiles.length > 0) {
      throw new Error(`ファイルサイズが上限(${appConfig.maxFileSizeMB}MB)を超えています: ${invalidSizeFiles.map(f => f.name).join(', ')}`);
    }

    const invalidExtFiles = files.filter(f => !validateFileExtension(f));
    if (invalidExtFiles.length > 0) {
      throw new Error(`許可されていない拡張子です: ${invalidExtFiles.map(f => f.name).join(', ')}`);
    }

    setIsUploading(true);
    setUploadProgresses(files.map(f => ({
      fileName: f.name.normalize('NFC'),
      progress: 0,
      status: 'pending',
    })));

    try {
      const uploadPromises = files.map(async (file, i) => {
        const normalizedFileName = file.name.normalize('NFC');
        const key = `${currentPrefix}${normalizedFileName}`;

        // 進捗更新: アップロード開始
        setUploadProgresses(prev => {
          const newProgresses = [...prev];
          newProgresses[i] = { ...newProgresses[i], status: 'uploading' };
          return newProgresses;
        });

        if (isMockMode) {
          // モックモード: 遅延してシミュレート
          for (let progress = 0; progress <= 100; progress += 20) {
            await new Promise(resolve => setTimeout(resolve, 200));
            setUploadProgresses(prev => {
              const newProgresses = [...prev];
              newProgresses[i] = { ...newProgresses[i], progress };
              return newProgresses;
            });
          }

          // モックデータに追加
          const data = getMockData(currentPrefix);
          data.objects.push({
            key,
            name: normalizedFileName,
            size: file.size,
            lastModified: new Date().toISOString(),
            isFolder: false,
          });

          // 進捗更新: 完了
          setUploadProgresses(prev => {
            const newProgresses = [...prev];
            newProgresses[i] = { ...newProgresses[i], status: 'completed', progress: 100 };
            return newProgresses;
          });
        } else {
          try {

            // 署名付きURL取得
            const urlResult = await getUploadUrl(key, file.type || 'application/octet-stream');
            if (!urlResult.success || !urlResult.data) {
              throw new Error(urlResult.error || 'アップロードURLの取得に失敗しました');
            }

            // S3にアップロード
            await uploadFileToS3(urlResult.data.uploadUrl, file, (progress) => {
              setUploadProgresses(prev => {
                const newProgresses = [...prev];
                newProgresses[i] = { ...newProgresses[i], progress };
                return newProgresses;
              });
            });

            // 進捗更新: 完了
            setUploadProgresses(prev => {
              const newProgresses = [...prev];
              newProgresses[i] = { ...newProgresses[i], status: 'completed', progress: 100 };
              return newProgresses;
            });
          } catch (err: unknown) {
            // 進捗更新: エラー
            setUploadProgresses(prev => {
              const newProgresses = [...prev];
              newProgresses[i] = { ...newProgresses[i], status: 'error', error: getErrorMessage(err, 'エラーが発生しました') };
              return newProgresses;
            });
          }
        }
      });

      // すべてのファイルのアップロードを並列で待機
      await Promise.all(uploadPromises);

      refresh();
      return true;
    } finally {
      setIsUploading(false);
    }
  }, [currentPrefix, refresh]);

  // オブジェクト削除
  const handleDeleteObjects = useCallback(async (keys: string[]): Promise<boolean> => {
    setIsDeleting(true);
    try {
      if (isMockMode) {
        // モックモード: ローカルから削除
        const data = getMockData(currentPrefix);
        data.objects = data.objects.filter(o => !keys.includes(o.key));
        data.folders = data.folders.filter(f => !keys.includes(f));
        setSelectedKeys([]);
        refresh();
        return true;
      }


      const result = await deleteObjects(keys);

      if (result.success) {
        setSelectedKeys([]);
        refresh();
        return true;
      } else {
        throw new Error(result.error || '削除に失敗しました');
      }
    } finally {
      setIsDeleting(false);
    }
  }, [currentPrefix, refresh]);

  // オブジェクト移動
  const handleMoveObject = useCallback(async (sourceKey: string, destinationPrefix: string): Promise<boolean> => {
    const isFolder = sourceKey.endsWith('/');
    const itemName = sourceKey.split('/').filter(Boolean).pop() || '';
    const normalizedDestPrefix = destinationPrefix.normalize('NFC');
    const destinationKey = isFolder
      ? `${normalizedDestPrefix}${itemName}/`
      : `${normalizedDestPrefix}${itemName}`;

    // 同一キーへの移動を防止（S3のCopy→Deleteで消失するため）
    if (sourceKey === destinationKey) {
      throw new Error('移動先が現在と同じフォルダです');
    }

    setIsMoving(true);
    try {
      if (isMockMode) {
        // モックモード: 移動をシミュレート
        alert(`モックモード: ${sourceKey} を ${destinationKey} に移動`);
        return true;
      }

      const result = isFolder
        ? await renameFolder(sourceKey, destinationKey)
        : await moveObject(sourceKey, destinationKey);

      if (result.success) {
        refresh();
        return true;
      } else {
        throw new Error(result.error || '移動に失敗しました');
      }
    } finally {
      setIsMoving(false);
    }
  }, [refresh]);

  // オブジェクト名変更 (リネーム)
  const handleRenameObject = useCallback(async (sourceKey: string, newName: string): Promise<boolean> => {
    const isFolder = sourceKey.endsWith('/');
    const parts = sourceKey.split('/').filter(Boolean);
    if (parts.length === 0) return false;
    
    // 親のプレフィックスパス
    const parentPrefix = sourceKey.substring(0, sourceKey.length - parts[parts.length - 1].length - (isFolder ? 1 : 0));
    
    const normalizedNewName = newName.trim().normalize('NFC');
    const destinationKey = isFolder 
      ? `${parentPrefix}${normalizedNewName}/`
      : `${parentPrefix}${normalizedNewName}`;

    if (sourceKey === destinationKey) {
      return true;
    }

    try {
      if (isMockMode) {
        if (isFolder) {
          const data = getMockData(currentPrefix);
          data.folders = data.folders.map(f => f === sourceKey ? destinationKey : f);
        } else {
          const data = getMockData(currentPrefix);
          data.objects = data.objects.map(o => o.key === sourceKey ? { ...o, key: destinationKey, name: newName } : o);
        }
        refresh();
        return true;
      }

      if (isFolder) {
        const result = await renameFolder(sourceKey, destinationKey);
        if (result.success) {
          refresh();
          return true;
        } else {
          throw new Error(result.error || 'フォルダ名の変更に失敗しました');
        }
      } else {
        const result = await moveObject(sourceKey, destinationKey);
        if (result.success) {
          refresh();
          return true;
        } else {
          throw new Error(result.error || 'ファイル名の変更に失敗しました');
        }
      }
    } catch (err: unknown) {
      console.error('Failed to rename object:', err);
      throw err;
    }
  }, [currentPrefix, refresh]);

  // ダウンロードURL取得
  const handleGetDownloadUrl = useCallback(async (key: string): Promise<string> => {
    if (isMockMode) {
      // モックモード: ダミーURL
      alert('モックモードではダウンロードできません');
      return '';
    }

    const result = await getDownloadUrl(key);

    if (result.success && result.data) {
      return result.data.downloadUrl;
    } else {
      throw new Error(result.error || 'ダウンロードURLの取得に失敗しました');
    }
  }, []);

  // 選択解除
  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  // アップロード進捗クリア
  const clearUploadProgresses = useCallback(() => {
    setUploadProgresses([]);
  }, []);

  return {
    // データ
    objects,
    folders,
    isLoading,
    error: error?.message || data?.error || null,

    // 選択状態
    selectedKeys,
    setSelectedKeys,
    clearSelection,

    // アップロード
    uploadProgresses,
    isUploading,
    handleUploadFiles,
    clearUploadProgresses,

    // 操作
    handleCreateFolder,
    handleDeleteObjects,
    handleMoveObject,
    handleRenameObject,
    handleGetDownloadUrl,
    isDeleting,
    isMoving,

    // その他
    refresh,
    refreshKey,
  };
}

export default useS3Manager;
